import { useCallback, useRef } from 'react';
import { isNil } from 'lodash';
import { CapTablesService } from 'api';
import { PREFERRED_STOCK } from 'common/constants/securityTypes';
import { CaptableColumn } from 'pages/CapTable/cap-table/components/captable-table/CaptableTable.props';
import { Logger } from 'utilities';
import { MULTIPLE_INVESTMENTS_TYPE } from './useRecalculateSecurities.constants';
import { RecalculationType, UseRecalculateSecuritiesParams } from './useRecalculateSecurities.types';

// CaptableColumn is the most common type for securities and it's defined in frontend app
// so we need to cast input and results to that type.
const useRecalculateSecurities = ({ capTableId, measurementDate }: UseRecalculateSecuritiesParams) => {
  const isRecalculatingRef = useRef(false);
  const isAcceptingQueueRef = useRef(false);
  const queuedSecuritiesRef = useRef<CaptableColumn[] | null>(null);
  const activeRecalculationRef = useRef<Promise<CaptableColumn[] | undefined> | null>(null);

  const fetchRecalculation = useCallback(
    async (securities: CaptableColumn[]) => {
      const response = await CapTablesService.apiCapTablesRecalculateSecuritiesCreate({
        cap_table: Number(capTableId),
        measurement_date: measurementDate,
        securities: securities as any,
      });
      return (response.securities ?? []) as unknown as CaptableColumn[];
    },
    [capTableId, measurementDate]
  );

  const recalculate = useCallback(
    async (securities: CaptableColumn[]) => {
      if (isRecalculatingRef.current) {
        if (isAcceptingQueueRef.current) {
          // Coalesce overlapping edits into one trailing recalc; last queued edit wins.
          queuedSecuritiesRef.current = securities;
          return activeRecalculationRef.current ?? undefined;
        }

        // The active run is closing; wait for it and start a fresh run for this edit.
        await activeRecalculationRef.current?.catch(() => undefined);
        return recalculate(securities);
      }
      isRecalculatingRef.current = true;
      isAcceptingQueueRef.current = true;

      const executeRecalculation = async () => {
        // init to request recalculation
        let currentSecurities: CaptableColumn[] | null = securities;
        let latestResult: CaptableColumn[] | undefined;

        while (currentSecurities) {
          // Requests must stay sequential to process the latest queued payload correctly.
          // eslint-disable-next-line no-await-in-loop
          latestResult = await fetchRecalculation(currentSecurities);
          // expected to be
          // empty in simple isolated run,
          // non-empty if calls were queued while recalculation was in progress
          currentSecurities = queuedSecuritiesRef.current;
          queuedSecuritiesRef.current = null;
        }

        // Stop accepting queued payloads before resolve so late edits cannot
        // attach to this closing run.
        isAcceptingQueueRef.current = false;

        return latestResult;
      };

      activeRecalculationRef.current = executeRecalculation();
      try {
        return await activeRecalculationRef.current;
      } catch (e) {
        Logger.error('Error recalculating securities', e);
      } finally {
        // Silently ignore errors — the user can still save and the backend will
        // recalculate on persist if needed. Avoiding a noisy error here as it would be confusing.
        // Best-effort semantics: do not carry queued work across runs after
        // completion or failure.
        queuedSecuritiesRef.current = null;
        isAcceptingQueueRef.current = false;
        isRecalculatingRef.current = false;
        activeRecalculationRef.current = null;
      }
    },
    [fetchRecalculation]
  );

  const validateRecalculationInput = useCallback(
    ({ column, recalculationType }: { column?: CaptableColumn; recalculationType: RecalculationType }) => {
      if (recalculationType === MULTIPLE_INVESTMENTS_TYPE) {
        const hasMultiple = Boolean(column?.has_multiple_investments);
        const hasDividends = Boolean(column?.has_dividends);
        const multipleInvestmentsLedgerIsPopulated = (column?.multiple_investments ?? []).length !== 0;
        const paymentTypeDefined = !isNil(column?.dividend_payment_type);
        return (
          column &&
          Number(column.security_type) === PREFERRED_STOCK &&
          hasMultiple &&
          hasDividends &&
          multipleInvestmentsLedgerIsPopulated &&
          paymentTypeDefined
        );
      }
      return false;
    },
    []
  );

  return {
    isRecalculatingRef,
    fetchRecalculation,
    recalculate,
    validateRecalculationInput,
  };
};

export default useRecalculateSecurities;
